// Estado do editor de marca d'agua: rascunho x salvo, troca de modelo com
// confirmacao, salvar e logo. A tela (WatermarkSection) so desenha.

import { useEffect, useState } from "react";
import {
  presetDesign,
  type WatermarkDesign,
  type WatermarkFlow,
  type WatermarkPresetId,
  type WatermarkSettings,
} from "@lucralink/shared";
import {
  useLogoUrl,
  useRemoveWatermarkLogo,
  useSaveWatermark,
  useUploadWatermarkLogo,
  useWatermarkPreview,
} from "@/lib/hooks/useWatermark";
import { isDesignDirty } from "@/lib/watermark";

export interface Draft {
  readonly enabled: boolean;
  readonly applyTo: "all" | "selected";
  readonly flows: readonly WatermarkFlow[];
  readonly design: WatermarkDesign;
}

export type Feedback = { readonly kind: "ok" | "error"; readonly text: string } | null;

export function draftOf(settings: WatermarkSettings): Draft {
  return {
    enabled: settings.enabled,
    applyTo: settings.applyTo,
    flows: settings.flows,
    design: settings.design,
  };
}

export function isDraftDirty(saved: Draft, draft: Draft): boolean {
  return (
    saved.enabled !== draft.enabled ||
    saved.applyTo !== draft.applyTo ||
    [...saved.flows].sort().join() !== [...draft.flows].sort().join() ||
    isDesignDirty(saved.design, draft.design)
  );
}

const SAVED_TEXT = "Marca d'água salva. Vale para os próximos envios.";
const NO_FLOW_TEXT = "Escolha pelo menos um fluxo ou aplique em todas as imagens.";

/** Resultado de uma operacao de logo: aviso nao bloqueante ou sucesso. */
function logoFeedback(success: string, warning: string | null): Feedback {
  return warning ? { kind: "error", text: `${success} ${warning}` } : { kind: "ok", text: success };
}

function useLogoActions(
  tenantId: string | null,
  logoPath: string | null,
  setFeedback: (f: Feedback) => void,
) {
  const upload = useUploadWatermarkLogo(tenantId, logoPath);
  const remove = useRemoveWatermarkLogo(logoPath);
  const onError = (error: Error) => setFeedback({ kind: "error", text: error.message });
  return {
    logoUrl: useLogoUrl(logoPath).data ?? null,
    busy: upload.isPending || remove.isPending,
    upload: (file: File) =>
      upload.mutate(file, {
        onSuccess: (w) => setFeedback(logoFeedback("Logo atualizado.", w)),
        onError,
      }),
    remove: () =>
      remove.mutate(undefined, {
        onSuccess: (w) => setFeedback(logoFeedback("Logo removido.", w)),
        onError,
      }),
  };
}

export function useWatermarkEditor(tenantId: string | null, settings: WatermarkSettings) {
  const saved = draftOf(settings);
  const [draft, setDraft] = useState<Draft>(saved);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingPreset, setPendingPreset] = useState<WatermarkPresetId | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const save = useSaveWatermark();
  const logo = useLogoActions(tenantId, settings.logoPath, setFeedback);
  const preview = useWatermarkPreview(draft.design, settings.logoPath, true);

  useEffect(() => setDraft(draftOf(settings)), [settings.version]);

  const patch = (change: Partial<Draft>) => setDraft((d) => ({ ...d, ...change }));
  const applyPreset = (id: WatermarkPresetId) => {
    patch({ design: presetDesign(id) });
    setSelectedId(null);
    setPendingPreset(null);
  };
  /** Edicoes nao salvas no desenho: confirma antes de trocar de modelo. */
  const pickPreset = (id: WatermarkPresetId) =>
    isDesignDirty(saved.design, draft.design) ? setPendingPreset(id) : applyPreset(id);

  const onSave = () => {
    if (draft.applyTo === "selected" && draft.flows.length === 0) {
      setFeedback({ kind: "error", text: NO_FLOW_TEXT });
      return;
    }
    setFeedback(null);
    save.mutate(draft, {
      onSuccess: () => setFeedback({ kind: "ok", text: SAVED_TEXT }),
      onError: (error) => setFeedback({ kind: "error", text: error.message }),
    });
  };

  return {
    draft,
    patch,
    dirty: isDraftDirty(saved, draft),
    saving: save.isPending,
    onSave,
    selectedId,
    setSelectedId,
    pendingPreset,
    cancelPreset: () => setPendingPreset(null),
    applyPreset,
    pickPreset,
    feedback,
    preview,
    logo,
  };
}
